import { Check, X } from 'lucide-react';
import { type CMState } from '@/hooks/useChangeManagements';
import { cn } from '@/lib/utils';

interface CMLifecycleStepperProps {
  state: CMState;
  className?: string;
}

const STAGES = [
  { key: 'draft', label: 'Draft' },
  { key: 'review', label: 'Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'executing', label: 'In progress' },
  { key: 'done', label: 'Done' },
] as const;

interface StageInfo {
  active: number;
  failed?: boolean;
  failedLabel?: string;
}

function stageInfo(state: CMState): StageInfo {
  switch (state) {
    case 'draft':
      return { active: 0 };
    case 'waiting_approval':
      return { active: 1 };
    case 'rejected':
      return { active: 1, failed: true, failedLabel: 'Rejected' };
    case 'approved':
      return { active: 2 };
    case 'in_progress':
    case 'paused':
    case 'verification':
      return { active: 3 };
    case 'completed':
    case 'partially_completed':
      return { active: 4 };
    case 'cancelled':
      return { active: 4, failed: true, failedLabel: 'Cancelled' };
    default:
      return { active: 0 };
  }
}

/**
 * Compact horizontal stepper for a change management's lifecycle
 * (Draft → Review → Approved → In progress → Done). Terminal failures
 * (rejected, cancelled) mark the stage they stalled at in red.
 */
export default function CMLifecycleStepper({ state, className }: CMLifecycleStepperProps) {
  const { active, failed, failedLabel } = stageInfo(state);

  return (
    <ol className={cn('flex items-center gap-1.5 overflow-x-auto', className)} aria-label="Change lifecycle">
      {STAGES.map((stage, i) => {
        const isCompleted = i < active;
        const isCurrent = i === active;
        const isFailedNode = isCurrent && failed;

        return (
          <li key={stage.key} className="flex items-center gap-1.5 shrink-0">
            <div
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold',
                isFailedNode && 'bg-sev-1/15 text-sev-1 ring-1 ring-sev-1/40',
                !isFailedNode && isCurrent && 'bg-brand-soft text-brand ring-1 ring-brand/40',
                isCompleted && 'bg-sev-4/15 text-sev-4',
                !isCompleted && !isCurrent && 'bg-muted text-muted-foreground',
              )}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {isFailedNode ? (
                <X className="h-3.5 w-3.5" />
              ) : isCompleted || (isCurrent && active === 4) ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                i + 1
              )}
            </div>
            <span
              className={cn(
                'text-xs whitespace-nowrap',
                isFailedNode && 'text-sev-1 font-medium',
                !isFailedNode && isCurrent && 'text-foreground font-medium',
                !isCurrent && 'text-muted-foreground',
              )}
            >
              {isFailedNode && failedLabel ? failedLabel : stage.label}
            </span>
            {i < STAGES.length - 1 ? (
              <span
                className={cn('mx-0.5 h-px w-5 sm:w-8', isCompleted ? 'bg-sev-4/40' : 'bg-border')}
                aria-hidden
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
